import { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { AlertCircle, ImageUp, Loader2, X } from "lucide-react";

import { Button } from "@/marketing/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import { cn } from "@/marketing/lib/utils";
import type { CampaignImageContentType } from "@/marketing/models/influencerCampaign";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import {
  ImageTooLargeError,
  getCroppedImageBlob,
} from "./cropImage";

const MAX_SIZE_BYTES = 1 * 1024 * 1024;
// Source guard before cropping; the cropped result is what must fit MAX_SIZE_BYTES.
const MAX_SOURCE_BYTES = 15 * 1024 * 1024;

const EXTENSION_BY_TYPE: Record<CampaignImageContentType, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

function isCampaignImageContentType(
  type: string,
): type is CampaignImageContentType {
  return type === "image/png" || type === "image/jpeg" || type === "image/webp";
}

interface ImageUploadFieldProps {
  value?: string | undefined;
  onChange: (cdnUrl: string | undefined) => void;
  label?: string | undefined;
  /** When true, the user crops the image before upload. Defaults to direct upload. */
  enableCrop?: boolean;
  /** Crop aspect ratio (width / height) when enableCrop is true. Defaults to 1 (square). */
  aspect?: number;
}

export function ImageUploadField({
  value,
  onChange,
  label = "Image",
  enableCrop = false,
  aspect = 1,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Crop modal state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropType, setCropType] = useState<CampaignImageContentType | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const croppedAreaRef = useRef<Area | null>(null);

  // Mirror cropSrc in a ref so the unmount-only cleanup can revoke it without
  // re-running (and revoking) on every cropSrc change mid-crop.
  const cropSrcRef = useRef<string | null>(null);
  cropSrcRef.current = cropSrc;

  const closeCropModal = useCallback((revoke = true) => {
    setCropSrc((prev) => {
      if (prev && revoke) URL.revokeObjectURL(prev);
      return null;
    });
    setCropType(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    croppedAreaRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (cropSrcRef.current) URL.revokeObjectURL(cropSrcRef.current);
    };
  }, []);

  const openCropModal = useCallback((file: File) => {
    setError(null);
    if (!isCampaignImageContentType(file.type)) {
      setError("Only PNG, JPEG, or WebP images are allowed");
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError("Image must be 15 MB or smaller");
      return;
    }
    setCropType(file.type);
    setCropSrc(URL.createObjectURL(file));
  }, []);

  const uploadBlob = useCallback(
    async (blob: Blob, contentType: CampaignImageContentType) => {
      setIsUploading(true);
      try {
        const fileName = `${crypto.randomUUID()}.${EXTENSION_BY_TYPE[contentType]}`;
        const { presignedUrl, cdnUrl } =
          await influencerCampaignAPIClient.createCampaignImagePresignedUrl({
            fileName,
            contentType,
            sizeBytes: blob.size,
          });
        const putResponse = await fetch(presignedUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: blob,
        });
        if (!putResponse.ok) {
          throw new Error(`Upload failed with status ${putResponse.status}`);
        }
        onChange(cdnUrl);
      } catch (err) {
        console.error("Campaign thumbnail upload failed:", err);
        setError("Upload failed. Please try again.");
      } finally {
        setIsUploading(false);
      }
    },
    [onChange],
  );

  // Direct upload (no crop): the original behaviour, enforcing the 1MB limit on the raw file.
  const uploadFileDirect = useCallback(
    async (file: File) => {
      setError(null);
      if (!isCampaignImageContentType(file.type)) {
        setError("Only PNG, JPEG, or WebP images are allowed");
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        setError("Image must be 1 MB or smaller");
        return;
      }
      await uploadBlob(file, file.type);
    },
    [uploadBlob],
  );

  const handleFileSelected = useCallback(
    (file: File) => {
      if (enableCrop) openCropModal(file);
      else void uploadFileDirect(file);
    },
    [enableCrop, openCropModal, uploadFileDirect],
  );

  const handleCropConfirm = useCallback(async () => {
    if (!cropSrc || !cropType || !croppedAreaRef.current) return;
    const src = cropSrc;
    const type = cropType;
    const area = croppedAreaRef.current;
    closeCropModal(false); // keep object URL alive until cropping finishes
    try {
      const blob = await getCroppedImageBlob(src, area, type, MAX_SIZE_BYTES);
      await uploadBlob(blob, type);
    } catch (err) {
      if (err instanceof ImageTooLargeError) {
        setError("Cropped image is too large. Try a tighter crop or smaller image.");
      } else {
        console.error("Image crop failed:", err);
        setError("Could not crop the image. Please try again.");
      }
    } finally {
      URL.revokeObjectURL(src);
    }
  }, [cropSrc, cropType, closeCropModal, uploadBlob]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    e.target.value = "";
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelected(file);
    },
    [handleFileSelected],
  );

  const handlePick = () => inputRef.current?.click();

  const handleRemove = () => {
    setError(null);
    onChange(undefined);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <label className="tw-block tw-text-xs tw-font-semibold tw-text-foreground/80">
        {label}
      </label>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleInputChange}
        className="tw-hidden"
      />

      {value ? (
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-input tw-bg-background tw-p-2">
          <img
            src={value}
            alt={label}
            className="tw-h-16 tw-w-16 tw-shrink-0 tw-rounded tw-object-cover"
          />
          <span className="tw-flex-1 tw-truncate tw-text-xs tw-text-muted-foreground" title={value}>
            {value}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePick}
            disabled={isUploading}
          >
            {isUploading && <Loader2 className="tw-mr-1.5 tw-h-3.5 tw-w-3.5 tw-animate-spin" />}
            Replace
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={isUploading}
            aria-label="Remove image"
          >
            <X className="tw-h-3.5 tw-w-3.5" />
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            "tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-md tw-border-2 tw-border-dashed tw-p-6 tw-transition-colors",
            isDragOver ? "tw-border-blue-500 tw-bg-blue-50" : "tw-border-muted",
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <>
              <Loader2 className="tw-mb-2 tw-h-6 tw-w-6 tw-animate-spin tw-text-muted-foreground" />
              <p className="tw-text-xs tw-text-muted-foreground">Uploading…</p>
            </>
          ) : (
            <>
              <ImageUp className="tw-mb-2 tw-h-7 tw-w-7 tw-text-muted-foreground" />
              <p className="tw-mb-1 tw-text-xs tw-font-medium">
                Drop an image here, or click to browse
              </p>
              <p className="tw-mb-3 tw-text-[11px] tw-text-muted-foreground">
                {enableCrop
                  ? "PNG, JPEG, or WebP · max 15 MB · cropped to 1 MB"
                  : "PNG, JPEG, or WebP · max 1 MB"}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={handlePick}>
                Choose image
              </Button>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-red-600">
          <AlertCircle className="tw-h-3.5 tw-w-3.5" />
          {error}
        </div>
      )}

      <Dialog
        open={cropSrc !== null}
        onOpenChange={(open) => {
          if (!open) closeCropModal();
        }}
      >
        <DialogContent className="tw-max-w-lg">
          <DialogHeader>
            <DialogTitle>Crop image</DialogTitle>
          </DialogHeader>

          {cropSrc && (
            <div className="tw-relative tw-h-72 tw-w-full tw-overflow-hidden tw-rounded-md tw-bg-muted">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPixels) => {
                  croppedAreaRef.current = areaPixels;
                }}
              />
            </div>
          )}

          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-xs tw-text-muted-foreground">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="tw-flex-1"
              aria-label="Zoom"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => closeCropModal()}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => void handleCropConfirm()}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
